/* eslint-disable no-underscore-dangle */
import { Button, IconButton } from '@fountain/fountain-ui-components';
import { Icon, PlusOutlineIcon } from '@fountain/ripple/icon';
import { Button as MuiButton, Grid, Typography } from '@material-ui/core';
import { produce } from 'immer';
import React, { FC, useContext } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from 'react-beautiful-dnd';
import { FormattedMessage, useIntl } from 'react-intl';
import { v4 as uuid } from 'uuid';

import { StageSettingCardEmptyState } from 'containers/WorkflowEditor/components/StageSettingCardEmptyState';
import {
  StageContext,
  StageContextData,
} from 'containers/WorkflowEditor/contexts/stageContext';
import { RulesEditDataContextProvider } from 'containers/WorkflowEditor/RulesEditDataContextProvider/RulesEditDataContextProvider';

import { DEFAULT_PARTNER_RULE } from './constants';
import { messages } from './messages';
import { PartnerRuleCard } from './PartnerRuleCard';
import { useStyles } from './styles';
import { PartnerRuleProps } from './types';

export const PartnerRules: FC = () => {
  const styles = useStyles();
  const intl = useIntl();

  const {
    stage: { external_id: externalId },
    partnerRules,
    setPartnerRules,
    updateStageResult,
  } = useContext<StageContextData>(StageContext);

  const availableRules = { ...partnerRules };

  // Filter out any partnerRules that have _destroy set to true
  Object.keys(availableRules).forEach(ruleId => {
    if (availableRules[ruleId]._destroy) {
      delete availableRules[ruleId];
    }
  });

  const errors =
    (updateStageResult.isError && updateStageResult?.error?.errors) ||
    undefined;

  const onAddRule = () => {
    const maxPosition = Object.values(availableRules).reduce(
      (max, rule) => Math.max(max, rule.position ?? 0),
      0,
    );

    setPartnerRules(
      produce((draftRules: PartnerRuleProps) => {
        draftRules[uuid()] = {
          ...DEFAULT_PARTNER_RULE,
          position: maxPosition + 1,
        };
      }),
    );
  };

  const onDragEnd = ({ destination, source }: DropResult) => {
    if (!destination || destination.index === source.index) return;

    const orderedKeys = Object.keys(availableRules).sort(
      (a, b) =>
        (availableRules[a].position ?? 0) - (availableRules[b].position ?? 0),
    );
    const [removed] = orderedKeys.splice(source.index, 1);
    orderedKeys.splice(destination.index, 0, removed);

    setPartnerRules(
      produce((draftRules: PartnerRuleProps) => {
        orderedKeys.forEach((key, idx) => {
          draftRules[key].position = idx + 1;
        });
      }),
    );
  };

  const onDeleteRule = (ruleId: string | number) => {
    setPartnerRules(
      produce((draftRules: PartnerRuleProps) => {
        draftRules[ruleId]._destroy = true;
      }),
    );
  };

  if (Object.keys(availableRules).length === 0) {
    return (
      <StageSettingCardEmptyState
        stageSettingCardProps={{
          title: intl.formatMessage(messages.rule),
          variant: 'default',
        }}
        buttonComponent={
          <Button
            type="secondary"
            size="small"
            autoWidth
            onClick={onAddRule}
            disableRipple
            aria-label={intl.formatMessage(messages.addRule)}
          >
            <Typography variant="h4" color="primary">
              <FormattedMessage {...messages.addRule} />
            </Typography>
          </Button>
        }
      />
    );
  }

  return (
    <RulesEditDataContextProvider externalId={externalId}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="partner-rules-list">
          {provided => (
            <Grid ref={provided.innerRef} {...provided.droppableProps}>
              {(() => {
                const sortedKeys = Object.keys(availableRules).sort(
                  (a, b) =>
                    (availableRules[a].position ?? 0) -
                    (availableRules[b].position ?? 0),
                );
                return sortedKeys.map((ruleId, index) => {
                  const rule = availableRules[ruleId];

                  return (
                    <Draggable
                      key={ruleId}
                      draggableId={String(ruleId)}
                      index={index}
                    >
                      {draggableProvided => (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          style={{
                            ...draggableProvided.draggableProps.style,
                            marginBottom:
                              index < sortedKeys.length - 1 ? 24 : 0,
                          }}
                        >
                          <PartnerRuleCard
                            ruleId={ruleId}
                            rule={rule}
                            index={index}
                            setPartnerRules={setPartnerRules}
                            onDeleteRule={onDeleteRule}
                            errors={errors}
                            dragHandleProps={
                              draggableProvided.dragHandleProps
                            }
                          />
                        </div>
                      )}
                    </Draggable>
                  );
                });
              })()}
              {provided.placeholder}
            </Grid>
          )}
        </Droppable>
      </DragDropContext>
      <Grid container justify="center">
        <MuiButton
          onClick={onAddRule}
          aria-label={intl.formatMessage(messages.addRule)}
        >
          <IconButton
            size="small"
            primary
            className={styles.plusButton}
            component={Grid}
          >
            <Icon icon={PlusOutlineIcon} size="xs" color="inherit" />
          </IconButton>
          <Typography variant="h4" color="primary" className={styles.addRule}>
            <FormattedMessage {...messages.addRule} />
          </Typography>
        </MuiButton>
      </Grid>
    </RulesEditDataContextProvider>
  );
};
